import { Spin } from 'antd';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { LIBRARY_COPY, TEMPLATES_COPY } from '../copy';
import { useTemplateList } from '../templateQueries';
import { templateKeyQueryOf } from './templateTree';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] THE LIBRARY'S INDEX — it opens a template, as the review site's library opens on its
 *  first. `?t=t12` (the record page's "From template t12") opens that one instead.
 *
 *  ⚠️ `<Navigate replace>` WHILE RENDERING, never an effect: the redirect is the render's answer, and
 *  `replace` keeps the bare library URL out of the history so Back does not bounce through it again.
 *  Every other search parameter survives (`?year=`); `t` is spent here.
 *
 *  [amended TEMPLATE-FIND] `t` is read as a person writes a number — trimmed, `152` or `T152` as well as
 *  `t152` (`templateKeyQueryOf`, the tree filter's own reading) — and an empty `t` is no `t` at all.
 *  ⚠️ A NUMBER THE SET DOES NOT HOLD IS SAID, NEVER SILENTLY THE FIRST TEMPLATE: a stale or mistyped
 *  link opened t1 before, and a person reading t1 took it for the template they asked for. The URL stays
 *  as it is, so the number they followed is still in front of them.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const LibraryIndex = (): React.ReactElement => {
  const { setKey = '' } = useParams();
  const [searchParams] = useSearchParams();
  const list = useTemplateList(setKey, { pane: true });

  if (list.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  const items = list.data?.items ?? [];
  const typed = searchParams.get('t')?.trim() ?? '';
  const wanted = typed === '' ? null : (templateKeyQueryOf(typed) ?? typed);
  const first = [...items].sort((a, b) => a.ordinal - b.ordinal)[0];
  const target = wanted === null ? first : items.find((item) => item.key === wanted);
  if (!target)
    return <EmptyState title={wanted === null ? LIBRARY_COPY.noTemplates : TEMPLATES_COPY.notInSet(wanted)} />;
  const rest = new URLSearchParams(searchParams);
  rest.delete('t');
  const search = rest.toString();
  return (
    <Navigate
      replace
      to={{ pathname: `/templates/${setKey}/t/${target.id}`, search: search === '' ? '' : `?${search}` }}
    />
  );
};

export default LibraryIndex;
